import {useMemo} from 'react';
import type {
  ModuleEnum,
  UserGroupModulePermissionResponse} from 'src/api/ProactApiClient';
import {
  PermissionOperationEnum
} from 'src/api/ProactApiClient';
import {useAppSelector} from 'src/redux/hooks';

/**
 * Maps friendly names to standard permission operations.
 * These are automatically included for all modules.
 */
const standardOperations = {
  view: PermissionOperationEnum.View,
  create: PermissionOperationEnum.Create,
  update: PermissionOperationEnum.Update,
  delete: PermissionOperationEnum.Delete,
  import: PermissionOperationEnum.Import,
  export: PermissionOperationEnum.Export,
  archive: PermissionOperationEnum.Archive,
} as const;

/**
 * Maps friendly names to common custom operation names.
 * These are automatically included for all modules.
 */
const commonCustomOperations = {
  attachments: 'attachments',
  notes: 'notes',
  notifications: 'notifications',
  qrCode: 'qr_code',
} as const;

/**
 * Standard CRUD permissions - automatically available for all modules.
 */
export type StandardPermissions = {
  [K in keyof typeof standardOperations]: boolean;
};

/**
 * Common custom permissions - automatically available for all modules.
 */
export type CommonCustomPermissions = {
  [K in keyof typeof commonCustomOperations]: boolean;
};

/**
 * Base permissions every module gets automatically.
 */
export type BasePermissions = StandardPermissions & CommonCustomPermissions;

/**
 * Type for custom operations config - maps friendly names to operation name strings.
 */
export type CustomOperationsConfig = Record<string, string>;

/**
 * Checks if a permission exists in the user's permission list.
 */
function hasPermission(
  permissions: UserGroupModulePermissionResponse[],
  module: ModuleEnum,
  operation: PermissionOperationEnum,
  operationName?: string
): boolean {
  return permissions.some(
    p =>
      p.module === module &&
      p.operation === operation &&
      (p.operationName === operationName ||
        (!p.operationName && !operationName))
  );
}

/**
 * Hook for getting module permissions with automatic standard and common custom operations.
 *
 * Standard operations (view, create, update, delete, import, export, archive) and
 * common custom operations (attachments, notes, notifications, qrCode) are automatically
 * included for all modules.
 *
 * For modules with additional custom operations, pass them as the second parameter.
 *
 * @example
 * // Simple module - no custom operations needed
 * const perms = useModulePermissions(ModuleEnum.Projects);
 * perms.view;   // boolean
 * perms.create; // boolean
 *
 * @example
 * // Module with custom operations
 * const TaskCustomOps = {
 *   transitionCreate: 'create',
 *   transitionComplete: 'complete',
 * } as const;
 *
 * const perms = useModulePermissions(ModuleEnum.Actions, TaskCustomOps);
 * perms.view;             // boolean (standard)
 * perms.transitionCreate; // boolean (custom)
 */
export function useModulePermissions<
  T extends CustomOperationsConfig = Record<string, never>,
>(
  module: ModuleEnum,
  customOperations?: T
): BasePermissions & {[K in keyof T]: boolean} {
  const permissions = useAppSelector(
    state => state.session.session?.user?.permissions ?? []
  );
  const isSuperAdmin = useAppSelector(state => state.session.isSuperAdmin);

  return useMemo(() => {
    const result: Record<string, boolean> = {};

    // Super admins bypass all module permission checks, matching the backend
    // ProactAuthorize attribute which authorizes super admins for every module.
    const check = (
      operation: PermissionOperationEnum,
      operationName?: string
    ) => isSuperAdmin || hasPermission(permissions, module, operation, operationName);

    // Check standard operations
    for (const [key, operation] of Object.entries(standardOperations)) {
      result[key] = check(operation);
    }

    // Check common custom operations
    for (const [key, operationName] of Object.entries(commonCustomOperations)) {
      result[key] = check(PermissionOperationEnum.Custom, operationName);
    }

    // Check module-specific custom operations
    if (customOperations) {
      for (const [key, operationName] of Object.entries(customOperations)) {
        result[key] = check(PermissionOperationEnum.Custom, operationName);
      }
    }

    return result as BasePermissions & {[K in keyof T]: boolean};
  }, [permissions, module, customOperations, isSuperAdmin]);
}
